// D-081: workspace authenticators. The setup steps (open the authenticator app or type the setup key,
// then the first 6-digit code), the one-time recovery codes, and My security (account menu): every
// authenticator app of the signed-in staff member, add another, remove one (never the last), and
// new recovery codes. Vendors keep their own two-step settings on the vendor admin.
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDateTime, normalizeDigits } from "@ady/format";
import { t } from "@ady/i18n";
import { Banner, Button, Field, Input, useToast } from "@ady/ui";
import { encode } from "uqr";
import { api, errorMessage, type AuthenticatorEnrolment } from "./api";
import { card, field, Panel } from "./ui";

export const ACCOUNT_TID = {
  setup: "console-authenticator-setup",
  setupKey: "console-setup-key",
  setupCode: "console-setup-code",
  setupConfirm: "console-setup-confirm",
  recoveryCodes: "console-recovery-codes",
  recoveryDone: "console-recovery-done",
  page: "console-my-security",
  app: (id: string) => `console-authenticator-${id}`,
  add: "console-authenticator-add",
} as const;

const AUTHENTICATORS_KEY = ["console", "authenticators"] as const;

/** The base32 secret inside an otpauth:// URI, in groups of four for typing. */
export function setupKeyOf(uri: string): string {
  const m = /[?&]secret=([^&]+)/i.exec(uri);
  const key = m?.[1] ? decodeURIComponent(m[1]) : "";
  return key.replace(/(.{4})(?=.)/g, "$1 ");
}

const sixDigits = (v: string) => normalizeDigits(v).replace(/\D/g, "").slice(0, 6);

/**
 * The otpauth URI as a QR code (uqr, drawn as one SVG path; no HTML injection). Always black on
 * white with a quiet zone, whatever the theme: authenticator apps need that contrast to scan it.
 */
function QrCode({ value, label }: { value: string; label: string }) {
  const { data } = encode(value, { ecc: "M", border: 2 });
  let d = "";
  data.forEach((row, y) =>
    row.forEach((on, x) => {
      if (on) d += `M${x} ${y}h1v1h-1z`;
    }),
  );
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${data.length} ${data.length}`}
      shapeRendering="crispEdges"
      className="size-44 rounded-xs"
    >
      <rect width="100%" height="100%" fill="white" />
      <path d={d} fill="black" />
    </svg>
  );
}

/**
 * Step 1: add the account to an authenticator app (a tap on a phone, or the setup key on a computer).
 * Step 2: the app's first code confirms it. `confirm` resolves with any one-time recovery codes.
 */
export function AuthenticatorSetup({
  enrolment,
  confirm,
  onConfirmed,
  confirmLabel,
}: {
  enrolment: AuthenticatorEnrolment;
  confirm: (code: string) => Promise<string[] | undefined>;
  onConfirmed: (recoveryCodes: string[] | undefined) => void;
  confirmLabel: string;
}) {
  const toast = useToast();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const key = setupKeyOf(enrolment.otpauth_uri);
  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(key.replace(/\s/g, ""));
      toast.show({ tone: "success", title: t("console.enroll.keyCopied") });
    } catch {
      toast.show({ tone: "error", title: t("team.inviteDialog.copyFailed") });
    }
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return setError(t("auth.codeInvalid"));
    setBusy(true);
    setError(undefined);
    try {
      onConfirmed(await confirm(code));
    } catch (err) {
      setError(errorMessage(err));
      setCode("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-3" data-testid={ACCOUNT_TID.setup}>
      <ol className="flex flex-col gap-3 text-sm">
        <li className="flex flex-col gap-2">
          <span>
            <span className="font-semibold">1. </span>
            {t("console.enroll.step1")}
          </span>
          <QrCode value={enrolment.otpauth_uri} label={t("console.enroll.qrLabel")} />
          <a
            href={enrolment.otpauth_uri}
            className="inline-flex min-h-control w-fit items-center rounded-xs border border-control px-3 text-sm font-medium text-link hover:bg-hover"
          >
            {t("console.enroll.openApp")}
          </a>
          <div className="flex flex-col gap-1 rounded-sm border border-subtle bg-sunken p-2.5">
            <span className="text-xs font-medium text-fg-secondary">
              {t("console.enroll.keyLabel")}
            </span>
            <span className="ady-ledger text-md break-all" data-testid={ACCOUNT_TID.setupKey}>
              {key}
            </span>
            <span className="text-xs text-fg-secondary">{t("console.enroll.keyHelp")}</span>
            <Button variant="secondary" icon="file" className="w-fit" onClick={copyKey}>
              {t("console.enroll.copyKey")}
            </Button>
          </div>
        </li>
        <li>
          <form noValidate onSubmit={submit} className="flex flex-col gap-3">
            <Field label={`2. ${t("console.enroll.codeLabel")}`} error={error} required>
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(sixDigits(e.target.value))}
                ledger
                data-testid={ACCOUNT_TID.setupCode}
              />
            </Field>
            <Button
              type="submit"
              icon="shieldCheck"
              loading={busy}
              data-testid={ACCOUNT_TID.setupConfirm}
            >
              {confirmLabel}
            </Button>
          </form>
        </li>
      </ol>
    </div>
  );
}

/** Shown once, right after they are made: ten single-use codes for a lost phone. */
export function RecoveryCodesPanel({
  codes,
  doneLabel,
  onDone,
}: {
  codes: string[];
  doneLabel: string;
  onDone: () => void;
}) {
  const toast = useToast();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      toast.show({ tone: "success", title: t("security.recoveryCopied") });
    } catch {
      toast.show({ tone: "error", title: t("team.inviteDialog.copyFailed") });
    }
  };
  return (
    <div
      className="flex flex-col gap-3 rounded-sm border border-warning-border bg-warning-bg p-3"
      data-testid={ACCOUNT_TID.recoveryCodes}
    >
      <p className="text-md font-semibold">{t("security.recoveryTitle")}</p>
      <p className="text-sm">{t("security.recoveryBody")}</p>
      <ol className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
        {codes.map((c) => (
          <li key={c} className="ady-ledger text-md">
            {c}
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-2">
        <Button icon="file" variant="secondary" onClick={copy}>
          {t("security.recoveryCopy")}
        </Button>
        <Button icon="check" onClick={onDone} data-testid={ACCOUNT_TID.recoveryDone}>
          {doneLabel}
        </Button>
      </div>
    </div>
  );
}

/** Account menu → My security. */
export function MySecurity() {
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({ queryKey: AUTHENTICATORS_KEY, queryFn: api.authenticators });
  const [adding, setAdding] = useState<"name" | AuthenticatorEnrolment | null>(null);
  const [name, setName] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [error, setError] = useState<string>();
  const refresh = () => qc.invalidateQueries({ queryKey: AUTHENTICATORS_KEY });

  const start = useMutation({
    mutationFn: () => api.authenticatorAdd(name.trim()),
    onSuccess: (e) => {
      setError(undefined);
      setAdding(e);
    },
    onError: (e) => setError(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.authenticatorRemove(id),
    onSuccess: async () => {
      setConfirmRemove(null);
      setError(undefined);
      toast.show({ tone: "success", title: t("console.mySecurity.removed") });
      await refresh();
    },
    onError: (e) => {
      setConfirmRemove(null);
      setError(errorMessage(e));
    },
  });
  const regenerate = useMutation({
    mutationFn: api.recoveryRegenerate,
    onSuccess: async (r) => {
      setConfirmRegenerate(false);
      setCodes(r.recovery_codes);
      await refresh();
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const closeAdd = () => {
    setAdding(null);
    setName("");
  };
  const apps = list.data?.authenticators ?? [];
  return (
    <div className="flex flex-col gap-3" data-testid={ACCOUNT_TID.page}>
      <h1 className="text-lg font-semibold">{t("console.mySecurity.title")}</h1>
      {error ? <Banner kind="error" title={error} /> : null}
      {codes ? (
        <RecoveryCodesPanel
          codes={codes}
          doneLabel={t("security.recoveryDone")}
          onDone={() => setCodes(null)}
        />
      ) : null}

      <Panel
        title={t("console.mySecurity.appsTitle")}
        aside={
          adding === null ? (
            <Button
              variant="secondary"
              icon="plus"
              onClick={() => setAdding("name")}
              data-testid={ACCOUNT_TID.add}
            >
              {t("console.mySecurity.add")}
            </Button>
          ) : null
        }
      >
        <p className="text-sm text-fg-secondary">{t("console.mySecurity.appsLead")}</p>
        {list.isError ? <Banner kind="error" title={errorMessage(list.error)} /> : null}
        <ul className="flex flex-col divide-y divide-subtle">
          {apps.map((a) => (
            <li
              key={a.id}
              className="flex flex-wrap items-center justify-between gap-2 py-2"
              data-testid={ACCOUNT_TID.app(a.id)}
            >
              <span className="flex flex-col">
                <span className="text-sm font-medium">{a.label}</span>
                <span className="text-xs text-fg-secondary">
                  {a.added_at
                    ? t("console.mySecurity.added", { date: formatDateTime(a.added_at) })
                    : null}
                  {" · "}
                  {a.last_used_at
                    ? t("console.mySecurity.lastUsed", { date: formatDateTime(a.last_used_at) })
                    : t("console.mySecurity.notUsed")}
                </span>
              </span>
              {confirmRemove === a.id ? (
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-xs">
                    {t("console.mySecurity.removeConfirm", { name: a.label })}
                  </span>
                  <Button variant="secondary" onClick={() => setConfirmRemove(null)}>
                    {t("console.mySecurity.keep")}
                  </Button>
                  <Button
                    variant="danger"
                    loading={remove.isPending}
                    onClick={() => remove.mutate(a.id)}
                  >
                    {t("console.mySecurity.remove")}
                  </Button>
                </span>
              ) : (
                <Button variant="secondary" onClick={() => setConfirmRemove(a.id)}>
                  {t("console.mySecurity.remove")}
                </Button>
              )}
            </li>
          ))}
        </ul>

        {adding === "name" ? (
          <form
            noValidate
            className={`${card} flex flex-col gap-2 p-3`}
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) start.mutate();
            }}
          >
            <label className="flex flex-col gap-1 text-sm font-medium">
              {t("console.mySecurity.nameLabel")}
              <input
                className={field}
                maxLength={40}
                value={name}
                placeholder={t("console.mySecurity.namePlaceholder")}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </label>
            <span className="flex gap-2">
              <Button type="submit" loading={start.isPending} disabled={!name.trim()}>
                {t("console.mySecurity.start")}
              </Button>
              <Button variant="secondary" onClick={closeAdd}>
                {t("console.mySecurity.cancel")}
              </Button>
            </span>
          </form>
        ) : adding ? (
          <div className={`${card} flex flex-col gap-2 p-3`}>
            <AuthenticatorSetup
              enrolment={adding}
              confirmLabel={t("console.mySecurity.add")}
              confirm={async (code) => (await api.authenticatorConfirm(code)).recovery_codes}
              onConfirmed={async (recovery) => {
                closeAdd();
                if (recovery?.length) setCodes(recovery);
                toast.show({ tone: "success", title: t("console.mySecurity.addedToast") });
                await refresh();
              }}
            />
            <Button variant="secondary" className="w-fit" onClick={closeAdd}>
              {t("console.mySecurity.cancel")}
            </Button>
          </div>
        ) : null}
      </Panel>

      <Panel title={t("console.mySecurity.recoveryTitle")}>
        <p className="text-sm">
          {t("console.mySecurity.recoveryLeft", {
            n: String(list.data?.recovery_codes_remaining ?? 0),
          })}
        </p>
        <p className="text-sm text-fg-secondary">{t("console.mySecurity.recoveryLead")}</p>
        {confirmRegenerate ? (
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-xs">{t("console.mySecurity.regenerateConfirm")}</span>
            <Button variant="secondary" onClick={() => setConfirmRegenerate(false)}>
              {t("console.mySecurity.keep")}
            </Button>
            <Button loading={regenerate.isPending} onClick={() => regenerate.mutate()}>
              {t("console.mySecurity.regenerate")}
            </Button>
          </span>
        ) : (
          <Button variant="secondary" className="w-fit" onClick={() => setConfirmRegenerate(true)}>
            {t("console.mySecurity.regenerate")}
          </Button>
        )}
      </Panel>
      <p className="text-xs text-fg-secondary">{t("console.mySecurity.vendorNote")}</p>
    </div>
  );
}
